import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Box, ResponsiveContext } from 'grommet';
import { Calendar, Globe, LineChart, StarOutline, Trigger } from 'grommet-icons';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { observer } from 'mobx-react-lite';
import { config } from 'src/config';
import { ApiError } from '/src/api';
import { ClientService } from '/src/api';
import {
  CurrentFacilityLocationsChart,
  DashboardHeader,
  DashboardInfoCard,
  EVStation,
  EnrollmentTrendsChart,
  EquipmentByFacilityChart,
  LoadingSpinner,
  RevenueByChart,
  RevenueByEquipmentChart,
  RevenueByProgramChart,
  RevenueByReportingPeriodChart,
  Tabs,
  Text,
} from '/src/components';
import { useClientStore, useDashboardStore, useGlobalStore, useUserStore } from '/src/context';
import { useProductTour } from '/src/hooks/useProductTour';
import { DesktopLayout } from '/src/layouts';
import { toastMessages } from '/src/lib/toast';
import { CountryId, CountryName } from '/src/lib/types';
import { formatNumber, getIsMobile, getPageTitle } from '/src/utils';
import { dashboardTour } from '/src/utils/productTours';

const Dashboard = observer(() => {
  /* Context */
  const globalStore = useGlobalStore();
  const userStore = useUserStore();
  const clientStore = useClientStore();
  const dashboardStore = useDashboardStore();
  const screenSize = useContext(ResponsiveContext);
  const { t } = useTranslation();

  /* State */
  const [isLoading, setIsLoading] = useState(false);
  const [isCanada, setIsCanada] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryName>(CountryName.USA);
  const [focusedCountryTab, setFocusedCountryTab] = useState('');
  const [searchParams] = useSearchParams();

  /* Computed */
  const isMobile = getIsMobile(screenSize);
  const urlClientId = searchParams.get('client');
  const clientId = urlClientId
    ? parseInt(urlClientId)
    : userStore.isExternalUser
      ? clientStore.activeClientId
      : undefined;

  useProductTour({
    tourName: 'dashboard-tour',
    steps: dashboardTour,
    autoStart: true,
    showOnce: true,
  });

  const isAdminDashboard = userStore.isFuseAdmin && !clientId;
  const isCombined = isAdminDashboard && selectedCountry === CountryName.Combined;
  const countryTabs = [CountryName.USA, CountryName.Canada, CountryName.Combined];
  const countryTabTitles = ['United States', 'Canada', 'Combined'];

  const getCountryName = (countryId: CountryId) => (countryId === CountryId.Canada ? 'Canada' : 'United States');

  useEffect(() => {
    if (clientId !== undefined) {
      clientStore.setSelectedClientId(clientId);
    } else {
      clientStore.setSelectedClientId(undefined);
    }
  }, [searchParams]);

  /* Methods */
  const fetchDashboard = async () => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      if (clientId) {
        await dashboardStore.getClientView(clientId);
        if (!userStore.isFacilityAdmin) {
          const client = await ClientService.get({ id: clientId });
          setIsCanada(client.country_id === CountryId.Canada);
        }
      } else if (selectedCountry === CountryName.Combined) {
        await dashboardStore.getCombinedAdminView();
        setIsCanada(false);
      } else {
        await dashboardStore.getAdminView(selectedCountry);
        setIsCanada(selectedCountry === CountryName.Canada);
      }
    } catch (err) {
      globalStore.handleApiError(err as ApiError, toastMessages.fetchDashboard.error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportPDF = async () => {
    const input = document.getElementById('dashboard-content');
    const facilityLocationsChart = document.getElementById('current_facility_locations');
    const exportButton = document.getElementById('export-pdf-button');

    if (input && facilityLocationsChart && exportButton) {
      facilityLocationsChart.style.display = 'none';
      exportButton.style.display = 'none';

      html2canvas(input, { scale: 2 }).then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgWidth = 210;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
        pdf.save('DashboardSummary.pdf');

        facilityLocationsChart.style.display = '';
        exportButton.style.display = '';
      });
    }
  };

  /* Effects */
  useEffect(() => {
    document.title = getPageTitle(config.client.basePageTitle, 'Dashboard');
    return () => dashboardStore.clearDashboardData();
  }, []);

  useEffect(() => {
    if (urlClientId) {
      const clientId = parseInt(urlClientId);
      if (!isNaN(clientId) && clientId !== clientStore.selectedClientId) {
        clientStore.setSelectedClientId(clientId);
      }
    } else if (userStore.isFuseAdmin && clientStore.selectedClientId !== 0) {
      const currentClientId = userStore.isExternalUser ? clientStore.activeClientId : clientStore.selectedClientId;
      if (currentClientId !== 0) {
        clientStore.setSelectedClientId(0);
      }
    }
  }, [
    urlClientId,
    clientStore,
    userStore.isFuseAdmin,
    userStore.isExternalUser,
    clientStore.activeClientId,
    clientStore.selectedClientId,
  ]);

  useEffect(() => {
    fetchDashboard();
  }, [clientId, selectedCountry]);

  /* Render */
  return (
    <DesktopLayout>
      {isLoading && <LoadingSpinner />}
      <Box flex="grow">
        {!isLoading && dashboardStore.dashboardData && (
          <Box
            flex="grow"
            gap="1rem"
            pad={{
              horizontal: isMobile ? '3.75rem' : '8rem',
              vertical: isMobile ? '2.25rem' : '3rem',
            }}
            animation={{ type: 'fadeIn', duration: 300 }}
            id="dashboard-content"
          >
            {dashboardStore.dashboardData.client_name && (
              <Box direction={isMobile ? 'column' : 'row'}>
                <DashboardHeader
                  clientName={dashboardStore.dashboardData.client_name}
                  clientId={dashboardStore.dashboardData.client_id}
                  onExportPDF={handleExportPDF}
                />
              </Box>
            )}
            {isAdminDashboard && (
              <Box direction="row" justify="start" border={{ side: 'bottom', color: 'light-5', size: 'small' }}>
                <Tabs
                  titles={countryTabTitles}
                  activeTab={countryTabs.indexOf(selectedCountry)}
                  focusedTab={focusedCountryTab}
                  setActiveTab={(tabIndex) => {
                    if (isLoading) return;
                    setSelectedCountry(countryTabs[tabIndex]);
                  }}
                  setFocusedTab={setFocusedCountryTab}
                  flex="shrink"
                  pad="none"
                  background="none"
                />
              </Box>
            )}
            {!isCombined && (
              <Box gap="1rem">
                <Box gap="1rem" direction={isMobile ? 'column' : 'row'}>
                  <Box id="kwh_consumed" title="kwh_consumed" width={isMobile ? undefined : `${100 / 3}%`}>
                    <DashboardInfoCard
                      value={
                        dashboardStore.dashboardData.kwh_consumed
                          ? formatNumber(dashboardStore.dashboardData.kwh_consumed)
                          : 0
                      }
                      title={t('dashboard:kwhConsumed')}
                      description={t('dashboard:energyConsumption')}
                      icon={<Trigger size="38px" color="light-1" />}
                    />
                  </Box>
                  <Box id="mt_co2e_displaced" width={isMobile ? undefined : `${100 / 3}%`}>
                    <DashboardInfoCard
                      value={
                        dashboardStore.dashboardData.mt_cO2e_displaced
                          ? formatNumber(dashboardStore.dashboardData.mt_cO2e_displaced)
                          : 0
                      }
                      title={t('dashboard:mtCo2eDisplaced')}
                      description={t('dashboard:equalToCreditsGeneratedToDate')}
                      icon={<Globe size="38px" color="light-1" />}
                    />
                  </Box>
                  <Box id="total_net_revenue_to_date" width={isMobile ? undefined : `${100 / 3}%`}>
                    <DashboardInfoCard
                      value={
                        dashboardStore.dashboardData.total_net_revenue
                          ? formatNumber(dashboardStore.dashboardData.total_net_revenue, true, true)
                          : 0
                      }
                      title={t('dashboard:totalNetRevenueToDate')}
                      description={t('dashboard:valueOfCreditsTransacted')}
                      icon={<LineChart size="38px" color="light-1" />}
                    />
                  </Box>
                </Box>
                <Box gap="1rem" direction={isMobile ? 'column' : 'row'}>
                  <Box id="avg_cost_per_kWh" width={isMobile ? undefined : isCanada ? '50%' : `${100 / 3}%`}>
                    <DashboardInfoCard
                      value={
                        dashboardStore.dashboardData.average_dollar_per_kwh
                          ? formatNumber(dashboardStore.dashboardData.average_dollar_per_kwh, true, true)
                          : 0
                      }
                      title={t('dashboard:avgCostPerKwh')}
                      subtitle="/ kWh"
                      icon={<StarOutline size="38px" color="light-1" />}
                    />
                  </Box>
                  <Box
                    id="enrolled_vehicles_and_equipment"
                    width={isMobile ? undefined : isCanada ? '50%' : `${100 / 3}%`}
                  >
                    <DashboardInfoCard
                      value={
                        dashboardStore.dashboardData.num_enrolled_equipment
                          ? formatNumber(dashboardStore.dashboardData.num_enrolled_equipment)
                          : 0
                      }
                      title={t('dashboard:enrolledVehiclesAndEquipment')}
                      subtitle="Units"
                      description={t('dashboard:totalNumberOfAssetsProducingCredits')}
                      icon={<EVStation />}
                    />
                  </Box>
                  {!isCanada && (
                    <Box id="days_to_next_credit_minting" width={isMobile ? undefined : `${100 / 3}%`}>
                      <DashboardInfoCard
                        value={
                          dashboardStore.dashboardData.days_to_next_credit_minting
                            ? formatNumber(dashboardStore.dashboardData.days_to_next_credit_minting)
                            : 0
                        }
                        title={t('dashboard:daysToNextCreditMinting')}
                        icon={<Calendar size="38px" color="light-1" />}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            )}
            {isCombined && (
              <Box gap="1rem">
                <Box gap="1rem" direction={isMobile ? 'column' : 'row'}>
                  <Box id="kwh_consumed" title="kwh_consumed" width={isMobile ? undefined : `${100 / 3}%`}>
                    <DashboardInfoCard
                      value={
                        dashboardStore.dashboardData.kwh_consumed
                          ? formatNumber(dashboardStore.dashboardData.kwh_consumed)
                          : 0
                      }
                      title={t('dashboard:kwhConsumed')}
                      description={t('dashboard:energyConsumption')}
                      icon={<Trigger size="38px" color="light-1" />}
                    />
                  </Box>
                  <Box id="mt_co2e_displaced" width={isMobile ? undefined : `${100 / 3}%`}>
                    <DashboardInfoCard
                      value={
                        dashboardStore.dashboardData.mt_cO2e_displaced
                          ? formatNumber(dashboardStore.dashboardData.mt_cO2e_displaced)
                          : 0
                      }
                      title={t('dashboard:mtCo2eDisplaced')}
                      description={t('dashboard:equalToCreditsGeneratedToDate')}
                      icon={<Globe size="38px" color="light-1" />}
                    />
                  </Box>
                  <Box id="enrolled_vehicles_and_equipment" width={isMobile ? undefined : `${100 / 3}%`}>
                    <DashboardInfoCard
                      value={
                        dashboardStore.dashboardData.num_enrolled_equipment
                          ? formatNumber(dashboardStore.dashboardData.num_enrolled_equipment)
                          : 0
                      }
                      title={t('dashboard:enrolledVehiclesAndEquipment')}
                      subtitle="Units"
                      description={t('dashboard:totalNumberOfAssetsProducingCredits')}
                      icon={<EVStation />}
                    />
                  </Box>
                </Box>
                {dashboardStore.dashboardData.countries?.map((country) => {
                  const baseData = dashboardStore.dashboardData;
                  if (!baseData) return null;
                  const isUSA = country.country_id === CountryId.USA;
                  const cardWidth = isMobile ? undefined : isUSA ? `${100 / 3}%` : '50%';
                  return (
                    <Box key={country.country_id} gap="0.75rem">
                      <Text size="large">
                        {getCountryName(country.country_id)}
                        {country.currency_code ? ` · ${country.currency_code}` : ''}
                      </Text>
                      <Box gap="1rem" direction={isMobile ? 'column' : 'row'}>
                        <Box width={cardWidth}>
                          <DashboardInfoCard
                            value={country.total_net_revenue ? formatNumber(country.total_net_revenue, true, true) : 0}
                            title={t('dashboard:totalNetRevenueToDate')}
                            description={t('dashboard:valueOfCreditsTransacted')}
                            icon={<LineChart size="38px" color="light-1" />}
                          />
                        </Box>
                        <Box width={cardWidth}>
                          <DashboardInfoCard
                            value={
                              country.average_dollar_per_kwh
                                ? formatNumber(country.average_dollar_per_kwh, true, true)
                                : 0
                            }
                            title={t('dashboard:avgCostPerKwh')}
                            subtitle="/ kWh"
                            icon={<StarOutline size="38px" color="light-1" />}
                          />
                        </Box>
                        {isUSA && (
                          <Box id="days_to_next_credit_minting" width={cardWidth}>
                            <DashboardInfoCard
                              value={
                                baseData.days_to_next_credit_minting
                                  ? formatNumber(baseData.days_to_next_credit_minting)
                                  : 0
                              }
                              title={t('dashboard:daysToNextCreditMinting')}
                              icon={<Calendar size="38px" color="light-1" />}
                            />
                          </Box>
                        )}
                      </Box>
                      <Box gap="1rem" direction={isMobile ? 'column' : 'row'}>
                        <Box width={isMobile ? undefined : '50%'}>
                          <RevenueByEquipmentChart
                            data={{
                              ...baseData,
                              percent_revenue_per_equipment_category: country.percent_revenue_per_equipment_category,
                            }}
                          />
                        </Box>
                        <Box width={isMobile ? undefined : '50%'}>
                          <RevenueByProgramChart
                            data={{
                              ...baseData,
                              percent_revenue_per_program: country.percent_revenue_per_program,
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
            {!isCombined && (
              <Box gap="1rem" direction={isMobile ? 'column' : 'row'}>
                <Box id="period_incentive_revenue" width={isMobile ? undefined : '50%'}>
                  <RevenueByReportingPeriodChart data={dashboardStore.dashboardData} />
                </Box>
                <Box id="enrollment_trends" width={isMobile ? undefined : '50%'}>
                  <EnrollmentTrendsChart data={dashboardStore.dashboardData} />
                </Box>
              </Box>
            )}
            {isAdminDashboard && !isCombined && (
              <Box gap="1rem" direction={isMobile ? 'column' : 'row'}>
                <Box id="revenue_by_equipment_type" width={isMobile ? undefined : '50%'}>
                  <RevenueByEquipmentChart data={dashboardStore.dashboardData} />
                </Box>
                <Box id="revenue_by_program" width={isMobile ? undefined : '50%'}>
                  <RevenueByProgramChart data={dashboardStore.dashboardData} />
                </Box>
              </Box>
            )}
            {!isAdminDashboard && (
              <Box gap="1rem" direction={isMobile ? 'column' : 'row'}>
                <Box id="revenue_by" width={isMobile ? undefined : '40%'}>
                  <RevenueByChart data={dashboardStore.dashboardData} />
                </Box>
                <Box id="equipment_by_facility" width={isMobile ? undefined : '60%'}>
                  <EquipmentByFacilityChart data={dashboardStore.dashboardData} />
                </Box>
              </Box>
            )}
            {!isAdminDashboard && (
              <Box id="current_facility_locations" gap="1rem" direction={isMobile ? 'column' : 'row'}>
                <CurrentFacilityLocationsChart data={dashboardStore.dashboardData} />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </DesktopLayout>
  );
});

export default Dashboard;
